import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, CalendarCheck } from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function CalendarMonth({ currentDate, setCurrentDate, selectedDate, setSelectedDate, events, todayTasks = [] }) {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const grid = useMemo(() => {
    const first = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0).getDate();
    // Monday-first index
    const startDow = (first.getDay() + 6) % 7;
    const cells = [];
    for (let i = 0; i < startDow; i++) {
      const d = new Date(year, month, -startDow + i + 1);
      cells.push({ date: d, inMonth: false });
    }
    for (let day = 1; day <= lastDay; day++) {
      cells.push({ date: new Date(year, month, day), inMonth: true });
    }
    while (cells.length % 7 !== 0 || cells.length < 42) {
      const last = cells[cells.length - 1].date;
      const d = new Date(last);
      d.setDate(d.getDate() + 1);
      cells.push({ date: d, inMonth: d.getMonth() === month });
      if (cells.length >= 42) break;
    }
    return cells;
  }, [year, month]);

  const eventsByDate = useMemo(() => {
    const map = {};
    for (const e of events) {
      (map[e.date] = map[e.date] || []).push(e);
    }
    return map;
  }, [events]);

  const today = new Date();
  const todayKey = fmtDate(today);
  const selectedKey = fmtDate(selectedDate);

  const prev = () => setCurrentDate(new Date(year, month - 1, 1));
  const next = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday = () => {
    setCurrentDate(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelectedDate(today);
  };

  return (
    <div
      className="rounded-2xl border border-border bg-card p-4 sm:p-6 shadow-sm"
      data-testid="calendar-month"
    >
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">
            {currentDate.toLocaleString("en-US", { month: "long" })}{" "}
            <span className="text-muted-foreground font-bold">{year}</span>
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {Object.keys(eventsByDate).filter((d) => d.startsWith(`${year}-${String(month + 1).padStart(2, "0")}`)).length}{" "}
            event days this month
          </p>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={goToday} className="rounded-xl" data-testid="cal-today">
            <CalendarCheck strokeWidth={1.5} className="size-4 mr-1.5" />
            Today
          </Button>
          <Button variant="ghost" size="icon" onClick={prev} className="rounded-xl" data-testid="cal-prev">
            <ChevronLeft strokeWidth={1.5} className="size-5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={next} className="rounded-xl" data-testid="cal-next">
            <ChevronRight strokeWidth={1.5} className="size-5" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAYS.map((d) => (
          <div key={d} className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center py-1">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1 sm:gap-2">
        {grid.map((cell) => {
          const key = fmtDate(cell.date);
          const isToday = key === todayKey;
          const isSelected = key === selectedKey;
          const isPast = cell.inMonth && cell.date < new Date(today.getFullYear(), today.getMonth(), today.getDate());
          const dayEvents = eventsByDate[key] || [];
          const openTask = isToday ? (todayTasks.find((t) => !t.completed) || todayTasks[0]) : null;
          const remainingTaskCount = isToday ? Math.max(0, todayTasks.filter((t) => !t.completed).length - (openTask && !openTask.completed ? 1 : 0)) : 0;
          return (
            <button
              key={key}
              onClick={() => setSelectedDate(cell.date)}
              data-testid={`calendar-day-${key}`}
              className={`cal-cell relative aspect-square sm:aspect-auto sm:min-h-[88px] rounded-xl border text-left p-1.5 sm:p-2 flex flex-col overflow-hidden ${
                cell.inMonth ? "bg-background" : "bg-secondary/30 text-muted-foreground"
              } ${
                isSelected
                  ? "border-primary ring-2 ring-primary/30"
                  : "border-border hover:border-primary/40"
              }`}
            >
              {/* Past-day diagonal slash overlay */}
              {isPast && !isToday && (
                <span
                  aria-hidden
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: "linear-gradient(to top left, transparent calc(50% - 0.5px), hsl(var(--muted-foreground) / 0.45) calc(50% - 0.5px), hsl(var(--muted-foreground) / 0.45) calc(50% + 0.5px), transparent calc(50% + 0.5px))",
                  }}
                />
              )}
              <div className="flex items-start justify-between gap-1">
                <span
                  className={`inline-flex items-center justify-center text-xs sm:text-sm font-semibold ${
                    isToday
                      ? "bg-primary text-primary-foreground rounded-full size-6 sm:size-7"
                      : ""
                  }`}
                >
                  {cell.date.getDate()}
                </span>
              </div>

              {/* Today: single task with click-through to /checklist */}
              {isToday && openTask && (
                <Link
                  to="/checklist"
                  onClick={(e) => e.stopPropagation()}
                  className="mt-1 mx-[-2px] block rounded-md bg-primary/10 hover:bg-primary/20 transition-colors px-1.5 py-1 text-[10px] sm:text-[11px] font-medium leading-tight truncate"
                  title={openTask.title}
                  data-testid={`calendar-today-task`}
                >
                  <span className={`${openTask.completed ? "line-through opacity-60" : ""}`}>
                    {openTask.title}
                  </span>
                </Link>
              )}
              {isToday && remainingTaskCount > 0 && (
                <Link
                  to="/checklist"
                  onClick={(e) => e.stopPropagation()}
                  className="mt-0.5 text-[10px] text-primary/80 hover:text-primary font-semibold leading-none"
                  data-testid="calendar-today-more-tasks"
                >
                  +{remainingTaskCount} more
                </Link>
              )}

              <div className="mt-auto flex flex-wrap gap-0.5 relative">
                {dayEvents.slice(0, 3).map((e) => (
                  <span
                    key={e.id}
                    className="size-1.5 sm:size-2 rounded-full bg-primary"
                    title={e.title}
                  />
                ))}
                {dayEvents.length > 3 && (
                  <span className="text-[10px] text-muted-foreground">+{dayEvents.length - 3}</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
